import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaCookieBite, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import './CookieConsent.css';

const STORAGE_KEY = 'zel_cookie_consent_v1';

const DEFAULT_PREFS = {
  necessary: true, // always on
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const CATEGORIES = [
  {
    key: 'necessary',
    locked: true,
    titleSk: 'Nevyhnutné',
    titleEn: 'Necessary',
    statusSk: 'Vždy aktívne',
    statusEn: 'Always Active',
    descSk: 'Nevyhnutné cookies sú potrebné pre základné funkcie tejto stránky, ako je zabezpečené prihlasovanie alebo úprava vašich preferencií. Tieto cookies neukladajú žiadne osobne identifikovateľné údaje.',
    descEn: 'Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.',
  },
  {
    key: 'functional',
    titleSk: 'Funkčné',
    titleEn: 'Functional',
    descSk: 'Funkčné cookies pomáhajú vykonávať určité funkcie, ako je zdieľanie obsahu stránky na sociálnych médiách alebo zhromažďovanie spätnej väzby.',
    descEn: 'Functional cookies help to perform certain functionalities like sharing the content of the website on social media platforms or collecting feedback.',
  },
  {
    key: 'analytics',
    titleSk: 'Analytické',
    titleEn: 'Analytics',
    descSk: 'Analytické cookies sa používajú na pochopenie toho, ako návštevníci interagujú so stránkou. Pomáhajú nám zlepšovať používateľský zážitok.',
    descEn: 'Analytical cookies are used to understand how visitors interact with the website. They help us improve the user experience.',
  },
  {
    key: 'marketing',
    titleSk: 'Marketingové',
    titleEn: 'Marketing',
    descSk: 'Marketingové cookies sa používajú na poskytovanie relevantných reklám a marketingových kampaní pre návštevníkov.',
    descEn: 'Marketing cookies are used to deliver relevant ads and marketing campaigns to visitors.',
  },
];

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const isSk = i18n.language?.startsWith('sk');
  const lang = isSk ? 'Sk' : 'En';

  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Show after a small delay so it doesn't compete with hero
        setTimeout(() => setShowBanner(true), 1200);
      } else {
        setPrefs(JSON.parse(saved));
      }
    } catch (e) {
      setShowBanner(true);
    }
  }, []);

  const persist = (newPrefs) => {
    const data = { ...newPrefs, timestamp: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* localStorage blocked */ }
    setPrefs(data);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const acceptAll = () => persist({ necessary: true, functional: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, functional: false, analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  const reopenSettings = () => setShowPrefs(true);

  const togglePref = (key) => {
    if (key === 'necessary') return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  // Floating "manage cookies" chip after consent has been given
  if (!showBanner && !showPrefs) {
    return prefs.timestamp ? (
      <button className="cookie-chip" onClick={reopenSettings} aria-label="Cookie preferences">
        <FaCookieBite />
      </button>
    ) : null;
  }

  return (
    <>
      {/* Banner — first-time visitors */}
      {showBanner && !showPrefs && (
        <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
          <div className="cookie-banner__inner">
            <div className="cookie-banner__icon"><FaCookieBite /></div>
            <div className="cookie-banner__body">
              <h3>{isSk ? 'Používame cookies' : 'We use cookies'}</h3>
              <p>
                {isSk
                  ? 'Používame cookies, aby sme vám pomohli efektívne sa pohybovať a vykonávať určité funkcie. Pre detaily kliknite na "Prispôsobiť".'
                  : 'We use cookies to help you navigate efficiently and perform certain functions. Click "Customise" for details.'}
              </p>
            </div>
            <div className="cookie-banner__actions">
              <button className="cookie-btn cookie-btn--ghost" onClick={() => setShowPrefs(true)}>
                {isSk ? 'Prispôsobiť' : 'Customise'}
              </button>
              <button className="cookie-btn cookie-btn--outline" onClick={rejectAll}>
                {isSk ? 'Odmietnuť' : 'Reject All'}
              </button>
              <button className="cookie-btn cookie-btn--primary" onClick={acceptAll}>
                {isSk ? 'Prijať všetko' : 'Accept All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPrefs && (
        <div className="cookie-modal" role="dialog" aria-modal="true">
          <div className="cookie-modal__backdrop" onClick={() => prefs.timestamp && setShowPrefs(false)} />
          <div className="cookie-modal__panel">
            <div className="cookie-modal__head">
              <h2>{isSk ? 'Prispôsobiť cookie preferencie' : 'Customise Consent Preferences'}</h2>
              {prefs.timestamp && (
                <button className="cookie-modal__close" onClick={() => setShowPrefs(false)} aria-label="Close">
                  <FaTimes />
                </button>
              )}
            </div>

            <div className="cookie-modal__intro">
              <p>
                {isSk
                  ? 'Používame cookies, aby sme vám pomohli efektívne sa pohybovať a vykonávať určité funkcie. Detailné informácie o všetkých cookies nájdete pod jednotlivými kategóriami nižšie.'
                  : 'We use cookies to help you navigate efficiently and perform certain functions. You will find detailed information about all cookies under each consent category below.'}
              </p>
              <p>
                {isSk
                  ? 'Cookies, ktoré sú zaradené ako "Nevyhnutné", sú uložené vo vašom prehliadači, pretože sú nevyhnutné pre základné funkcie tejto stránky.'
                  : 'The cookies that are categorised as "Necessary" are stored on your browser as they are essential for enabling the basic functionalities of the site.'}
              </p>
            </div>

            <div className="cookie-categories">
              {CATEGORIES.map((cat) => {
                const isOpen = openCategory === cat.key;
                const enabled = prefs[cat.key];
                return (
                  <div key={cat.key} className="cookie-cat">
                    <div className="cookie-cat__head">
                      <button
                        className="cookie-cat__chevron"
                        onClick={() => setOpenCategory(isOpen ? null : cat.key)}
                        aria-expanded={isOpen}
                      >
                        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                        <span>{cat[`title${lang}`]}</span>
                      </button>

                      {cat.locked ? (
                        <span className="cookie-cat__locked">{cat[`status${lang}`]}</span>
                      ) : (
                        <label className="cookie-switch">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => togglePref(cat.key)}
                          />
                          <span className="cookie-switch__slider" />
                        </label>
                      )}
                    </div>

                    {isOpen && (
                      <p className="cookie-cat__desc">{cat[`desc${lang}`]}</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="cookie-modal__actions">
              <button className="cookie-btn cookie-btn--primary" onClick={acceptAll}>
                {isSk ? 'Prijať všetko' : 'Accept All'}
              </button>
              <button className="cookie-btn cookie-btn--outline" onClick={savePrefs}>
                {isSk ? 'Uložiť moje preferencie' : 'Save My Preferences'}
              </button>
              <button className="cookie-btn cookie-btn--outline" onClick={rejectAll}>
                {isSk ? 'Odmietnuť všetko' : 'Reject All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
